import { useEffect, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { Alert, Button, Input } from "reactstrap";
import { InputPicker } from "rsuite";
import { uniq } from "underscore";
import { useQueryClient } from "@tanstack/react-query";

import RichTextEditorV2 from "../CRM/RichTextEditorV2";
import AppDrawer from "./AppDrawer";
import { DrawerProps } from "./DrawerTypes";
import useQueriedRecords from "../../api/api-hooks/records/useQueriedRecords";
import AppSpinner from "../../elements/AppSpinner";
import { AppToggle } from "./Fields/CustomToggle";
import FieldUtils from "../../utils/FieldUtils";
import {
  emailRegex,
  emailValidationRegex,
} from "./EmailActionsDrawer/EmailActionsDrawer";
import { tenantIDAtom } from "../../atoms/GlobalAtoms";

interface EmailDrawerEmail {
  Name?: string;
  Email: string;
}

interface Props extends DrawerProps {
  subject?: string;
  email: string | undefined;
}

export default function EmailDrawer({ open, onClose, subject, email }: Props) {
  const [emailBody, setEmailBody] = useState("");
  const [subjectState, setSubject] = useState("");
  const [toEmails, setToEmails] = useState<EmailDrawerEmail[]>([]);
  const [ccEmails, setCcEmails] = useState<EmailDrawerEmail[]>([]);
  const [bccEmails, setBccEmails] = useState<EmailDrawerEmail[]>([]);

  useEffect(() => {
    if (!email && !subjectState) return;

    if (email) {
      const finalText = email.replace(/\r?\n/g, "<br />");
      setEmailBody(finalText);
    }

    if (subject) setSubject(subject);
  }, [email, subject]);

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle="Create Email"
      headerDescription="Review and send this email"
      headerOptions={
        <Button onClick={openEmail} size="sm" color="primary">
          Open Email
        </Button>
      }
      bodyCSS="pt-3"
      body={
        <>
          <EmailListGenerator
            title="To:"
            emails={toEmails}
            setEmails={setToEmails}
          />
          <EmailListGenerator
            title="CC:"
            emails={ccEmails}
            setEmails={setCcEmails}
          />
          <EmailListGenerator
            title="BCC:"
            emails={bccEmails}
            setEmails={setBccEmails}
          />
          <h4>Subject:</h4>
          <Input
            bsSize="sm"
            className="mb-4"
            placeholder="Add a subject"
            value={subjectState}
            onChange={(e) => setSubject(e.target.value)}
          />
          <h4>Email Body</h4>
          <RichTextEditorV2
            isEditing={true}
            value={emailBody}
            onChange={setEmailBody}
            useBrie={false}
          />
        </>
      }
    />
  );

  function openEmail() {
    const res = emailBody
      .replace(/<br\s*\/?>/gi, "\n\n") // Replace <br> or <br/> with newline
      .replace(/<[^>]+>/g, " ")
      .replace(/[^\S\r\n]+/g, " ") // Remove consecutive whitespace
      .trim();

    const body = encodeURIComponent(res);

    // This is a variable because I tried adding support for specific email clients in the browser, but it didn't work.
    const startingLink = "mailto:";

    const url = `${startingLink}${toEmails
      .map((e) => e.Email)
      .join(",")}?body=${body}&subject=${subjectState}${getEmailsQueryString(
      "cc",
      ccEmails
    )}${getEmailsQueryString("bcc", bccEmails)}
   `;

    window.open(url, "_newtab");
  }

  function getEmailsQueryString(type: string, emails: EmailDrawerEmail[]) {
    if (!emails?.length) return "";

    const finalEmails = encodeURIComponent(
      emails.map((e) => e.Email).join(",")
    );

    return `&${type}=${finalEmails}`;
  }

  function handleClose() {
    onClose();
  }
}

function EmailListGenerator({
  title,
  emails,
  setEmails,
}: {
  title: string;
  emails: EmailDrawerEmail[];
  setEmails: React.Dispatch<React.SetStateAction<EmailDrawerEmail[]>>;
}) {
  const [contactsSearch, setContactsSearch] = useState(false);

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h4 className="mb-0">{title}</h4>
        <div className="d-flex align-items-center gap-2">
          <small>Search Contacts</small>
          <AppToggle
            value={contactsSearch}
            handleChange={(e) => setContactsSearch(e.target.checked)}
          />
        </div>
      </div>
      {contactsSearch && (
        <ContactsInput
          emailsAdded={emails?.map((e) => e?.Email)}
          requiresEmail={true}
          onAdd={(newContact) => setEmails((prev) => [...prev, newContact])}
        />
      )}
      {!contactsSearch && (
        <EmailInput
          onAdd={(newEmails) => {
            const newContacts: EmailDrawerEmail[] = [];

            for (const email of newEmails) {
              if (emails?.find((e) => e.Email === email)) continue;
              newContacts.push({ Email: email });
            }

            setEmails((prev) => [...prev, ...newContacts]);
          }}
        />
      )}
      {emails?.length < 1 ? (
        <div
          className={`font-weight-500 small bg-ultralight rounded px-4 py-2 mb-4`}
        >
          No Emails Added
        </div>
      ) : (
        <div className="mb-3">
          {emails.map((email) => {
            return (
              <div
                key={email.Email}
                className="d-flex justify-content-between align-items-center bg-ultralight rounded px-3 mb-1"
              >
                <div className="text-xs font-weight-500 py-1">
                  <div
                    className={`${email?.Name ? "text-primary" : ""} text-sm`}
                  >
                    {email?.Name || email.Email}
                  </div>
                  {email?.Name && <div>{email.Email}</div>}
                </div>
                <div>
                  <i
                    className="mdi mdi-close text-danger text-lg cursor-pointer"
                    onClick={() =>
                      setEmails((prev) =>
                        prev.filter((e) => e.Email !== email.Email)
                      )
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function ContactsInput({
  emailsAdded,
  requiresEmail,
  onAdd,
}: {
  emailsAdded: string[];
  requiresEmail: boolean;
  onAdd: (contact: EmailDrawerEmail) => void;
}) {
  const [query, setQuery] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const { records, recordsLoading: rLoading } = useQueriedRecords({
    moduleAPIName: "Contacts",
    query: query,
  });
  const timeoutId = useRef<NodeJS.Timeout>();
  const [value, setValue] = useState<any>(null);
  const [error, setError] = useState("");
  const client = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);

  const recordsLoading = loading || rLoading;
  let finalRecords = records;

  if (!records || !records.length || !search) {
    const allContacts = client
      .getQueriesData({ queryKey: [tenantID, "records", "Contacts"] })
      .flatMap((q: any) => {
        const data = q[1]?.records;

        if (!data) return [];

        return data;
      });

    finalRecords = uniq(allContacts, "_id");
  }

  return (
    <>
      <div className="d-flex align-items-center mb-2">
        <InputPicker
          className="w-100"
          placeholder="Search for Contacts"
          data={finalRecords}
          labelKey="_RecordName"
          valueKey="_id"
          onSearch={handleSearch}
          value={value}
          onChange={setValue}
          onExit={() => setSearch("")}
          renderMenu={(menu) => {
            if (recordsLoading && search) {
              return (
                <p className="d-flex justify-content-center pt-3">
                  <AppSpinner />
                </p>
              );
            }
            return menu;
          }}
        />
        <Button
          size="sm"
          color="primary"
          style={{ width: 80 }}
          className="ml-3"
          onClick={handleAdd}
        >
          Add
        </Button>
      </div>
      {error && (
        <Alert color="danger" className="px-3 py-2">
          {error}
        </Alert>
      )}
    </>
  );

  function handleSearch(searchValue: string) {
    setError("");
    setSearch(searchValue);
    setLoading(true);

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    if (!searchValue) {
      setLoading(false);
      return setQuery(null);
    }

    timeoutId.current = setTimeout(() => {
      setLoading(false);
      setQuery({
        Fields: ["_id", "_RecordName", "Email"],
        Filters: [
          {
            Group: false,
            Field: "_RecordName",
            Method: "contains",
            Value: searchValue,
          },
        ],
        Pagination: {
          Count: 50,
          Page: 1,
        },
      });
    }, 1500);
  }

  function handleAdd() {
    setError("");

    const contact = finalRecords.find((r: any) => r?._id === value);

    if (requiresEmail && !contact?.Email) {
      return setError(
        `${contact?._RecordName} does not have an email saved in GrowthCloud.`
      );
    }

    const simplifiedContact: EmailDrawerEmail = {
      Name: contact._RecordName,
      Email: FieldUtils.getPrimaryOrFirstValue(contact.Email, "Email"),
    };

    if (emailsAdded.includes(simplifiedContact.Email)) {
      return setError(
        `${contact?._RecordName}'s email has already been added.`
      );
    }

    setValue(null);
    onAdd(simplifiedContact);
  }
}

function EmailInput({ onAdd }: { onAdd: (emails: string[]) => void }) {
  const [text, setText] = useState("");

  return (
    <div className="d-flex align-items-center mb-2">
      <Input
        bsSize="sm"
        style={{ height: 36 }}
        placeholder="bob@gmail.com,bill@gmail.com"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <Button
        size="sm"
        color="primary"
        style={{ width: 80 }}
        className="ml-3"
        onClick={handleAdd}
      >
        Add
      </Button>
    </div>
  );

  function handleAdd() {
    const emails = text.match(emailRegex);

    if (!emails) return;

    const validEmails = emails.filter((email) => {
      return emailValidationRegex.test(email);
    });

    const finalEmails = [...new Set([...validEmails])];

    setText("");
    onAdd(finalEmails);
  }
}
